/**
 * Check_Email_Screen.tsx — SK Platform
 * Shown right after Step 1 of signup. Step-1 signup issues a session cookie
 * (the account exists but is unverified and the org is still inactive). The
 * user opens the emailed link → Verify_Email_Screen verifies the address +
 * activates the org → they sign in → Step 2 ("Tell us about you") to finish.
 *
 * Advancing to login when the email is verified:
 *   1. Cross-tab signal (BroadcastChannel/localStorage) — instant, but only
 *      works when the verify link opens on the SAME origin as this tab. For
 *      products whose verification email lands on their own domain (e.g.
 *      catalog.*.softknack.com) the signal never reaches this platform tab.
 *   2. SSE push (GET /v1/auth/verify-stream) — the robust cross-origin/
 *      cross-device path. The tab holds one Server-Sent-Events connection,
 *      authenticated by the session cookie signup minted; the server pushes a
 *      single `verified` event the moment the backend confirms the address, so
 *      there's no polling. A slow GET /v1/me safety poll only kicks in when SSE
 *      is unavailable (native / old browser) or the stream closes fatally.
 *
 * Premium, hopeful tone — this is the first impression of SK Platform.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { View, Text, StyleSheet, ScrollView, Platform, Pressable, ActivityIndicator } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useNavigation, useRoute, type RouteProp } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { AuthStackParamList } from '../../navigation/types';
import { CONTENT } from '../../utils/responsive';
import { useTheme } from '../../context/themeContext';
import { onEmailVerified } from '../../navigation/authChannel';
import { getCurrentUser, invalidateMeCache, resendVerification } from '../../services/auth/auth';
import { Platform_Verify_Stream_Url } from '../../api-endpoints/api-endpoints';
import { handleApiError } from '../../utils/errorHandler';
import { showSuccess } from '../../utils/successHandler';
import AuthBrandHeader from '../../components/authBrandHeader';

// Fallback cooldown, used only until the server tells us the real window. The
// backend rate-limits resends (one per 3 min per user) and returns that window
// as `retry_after_seconds` on success, or `Retry-After` on a 429 — those are
// the source of truth. Persisted so a page refresh can't hand out a free resend.
const RESEND_COOLDOWN_MS = 3 * 60 * 1000; // 3 minutes (matches backend default)
// Verification is primarily learned via a Server-Sent-Events push (see the
// effect below). This poll is only a SAFETY NET — used when SSE is unavailable
// (native, or an old browser) or the stream closes fatally — so it runs at a
// slow cadence instead of the old aggressive 5s to keep /me load negligible.
const FALLBACK_POLL_INTERVAL_MS = 30_000;
// Stop the fallback poll after this many consecutive failures (e.g. no session).
const MAX_POLL_ERRORS = 3;

const cooldownKey = (email?: string) => `sk_verify_resend_until_${email || 'anon'}`;

// m:ss for the cooldown countdown.
const formatRemaining = (ms: number): string => {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

const Check_Email_Screen: React.FC = () => {
  const { theme } = useTheme();
  const { colors } = theme;
  const navigation = useNavigation<NativeStackNavigationProp<AuthStackParamList, 'checkEmail'>>();
  // The address signup was submitted with (shown so the user knows where to look).
  const { params } = useRoute<RouteProp<AuthStackParamList, 'checkEmail'>>();
  const email = params?.email;

  const [isSending, setIsSending] = useState(false);
  const [sentOnce, setSentOnce] = useState(false);
  // When the signup session is gone (e.g. a stale/expired tab), resend can't
  // work — we can only point the user back to login.
  const [sessionLost, setSessionLost] = useState(false);
  // Epoch ms until which resend is disabled; null = ready.
  const [cooldownUntil, setCooldownUntil] = useState<number | null>(null);
  // The full cooldown window (ms) the server handed us for the *current*
  // cooldown, so the progress bar fills against the real window (3 min) rather
  // than a hardcoded constant.
  const [cooldownWindowMs, setCooldownWindowMs] = useState(RESEND_COOLDOWN_MS);
  const [now, setNow] = useState(() => Date.now());

  // Guards double-navigation when the cross-tab signal and the poll both fire.
  const doneRef = useRef(false);
  // Synchronous guard against double-fire: `isSending` is async React state, so
  // two fast taps can both pass the `isSending` check before the first re-render
  // and send two emails. A ref flips immediately, so the second tap is a no-op.
  const sendingRef = useRef(false);

  const advanceToLogin = useCallback(() => {
    if (doneRef.current) return;
    doneRef.current = true;
    navigation.reset({ index: 0, routes: [{ name: 'login' }] });
  }, [navigation]);

  // 1) Instant path: the verify tab broadcasts "email verified" (same-origin).
  useEffect(() => onEmailVerified(() => advanceToLogin()), [advanceToLogin]);

  // 2) Primary path: a Server-Sent-Events stream pushes "verified" the instant
  //    the backend confirms the address — one idle connection, zero requests
  //    while waiting (replaces the old 5s /v1/me poll that re-ran auth + a DB
  //    read every tick and became a thundering herd during signup spikes).
  //    A slow safety poll is used ONLY when SSE can't be (native / old browser)
  //    or the stream closes fatally (e.g. the signup session expired).
  useEffect(() => {
    let cancelled = false;
    let es: EventSource | null = null;
    let pollId: ReturnType<typeof setInterval> | null = null;
    let pollErrors = 0;

    const stopPoll = () => {
      if (pollId) { clearInterval(pollId); pollId = null; }
    };

    const pollOnce = async () => {
      try {
        invalidateMeCache(); // bypass the 5s /v1/me cache so we see the flip promptly
        const res: any = await getCurrentUser();
        const verified = (res?.data ?? res)?.user?.email_verified;
        if (!cancelled && verified) advanceToLogin();
        pollErrors = 0;
      } catch (err: any) {
        // A 401 means signup's session is gone — polling (and resend) are moot.
        if (err?.response?.status === 401) {
          if (!cancelled) setSessionLost(true);
          stopPoll();
          return;
        }
        if (++pollErrors >= MAX_POLL_ERRORS) stopPoll();
      }
    };

    const startFallbackPoll = () => {
      if (pollId || cancelled) return;
      void pollOnce(); // immediate catch-up, then the slow cadence
      pollId = setInterval(pollOnce, FALLBACK_POLL_INTERVAL_MS);
    };

    // EventSource is a browser API; native RN has no equivalent here.
    const canUseSse = Platform.OS === 'web' && typeof EventSource !== 'undefined';
    if (!canUseSse) {
      startFallbackPoll();
      return () => { cancelled = true; stopPoll(); };
    }

    try {
      // withCredentials so the httpOnly session cookie rides cross-origin
      // (FE :3009 → API :3013), exactly as axios reaches /me.
      es = new EventSource(Platform_Verify_Stream_Url, { withCredentials: true });
      es.addEventListener('verified', () => { if (!cancelled) advanceToLogin(); });
      // Server capped the stream at link-expiry (15 min) — degrade to the poll.
      es.addEventListener('timeout', () => { es?.close(); startFallbackPoll(); });
      es.onopen = () => { stopPoll(); }; // stream healthy → no polling needed
      es.onerror = () => {
        // EventSource silently auto-reconnects while CONNECTING; only a CLOSED
        // readyState is terminal (auth failure / unsupported) — poll then.
        if (es && es.readyState === EventSource.CLOSED) startFallbackPoll();
      };
    } catch {
      startFallbackPoll();
    }

    return () => {
      cancelled = true;
      es?.close();
      stopPoll();
    };
  }, [advanceToLogin]);

  // Persist + apply a cooldown window (seconds). Single entry point used by
  // both the success path (server's retry_after_seconds) and the 429 path
  // (Retry-After header), so the FE countdown always mirrors the backend.
  const startCooldown = useCallback(async (windowSec: number) => {
    const windowMs = Math.max(1, windowSec) * 1000;
    const until = Date.now() + windowMs;
    setCooldownWindowMs(windowMs);
    setCooldownUntil(until);
    setNow(Date.now());
    try {
      await AsyncStorage.setItem(cooldownKey(email), JSON.stringify({ until, windowMs }));
    } catch { /* ignore */ }
  }, [email]);

  // Restore any in-flight cooldown so a refresh doesn't hand out a free resend.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const raw = await AsyncStorage.getItem(cooldownKey(email));
        if (!raw) return;
        // New format is JSON {until, windowMs}; tolerate the old bare-number form.
        let until = 0;
        let windowMs = RESEND_COOLDOWN_MS;
        try {
          const parsed = JSON.parse(raw);
          until = Number(parsed?.until) || 0;
          windowMs = Number(parsed?.windowMs) || RESEND_COOLDOWN_MS;
        } catch {
          until = parseInt(raw, 10) || 0;
        }
        if (active && until > Date.now()) {
          setCooldownWindowMs(windowMs);
          setCooldownUntil(until);
        }
      } catch { /* ignore */ }
    })();
    return () => { active = false; };
  }, [email]);

  // Tick a 1s clock only while a cooldown is active (drives the countdown label).
  useEffect(() => {
    if (!cooldownUntil) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [cooldownUntil]);

  const remainingMs = cooldownUntil ? cooldownUntil - now : 0;
  const onCooldown = remainingMs > 0;
  // Elapsed fraction (0→100) so the progress bar fills as the cooldown runs out.
  const cooldownPct = onCooldown
    ? Math.max(0, Math.min(100, 100 - (remainingMs / cooldownWindowMs) * 100))
    : 100;

  // Clear the cooldown state once it elapses.
  useEffect(() => {
    if (cooldownUntil && remainingMs <= 0) setCooldownUntil(null);
  }, [cooldownUntil, remainingMs]);

  const handleResend = async () => {
    // Synchronous double-click guard (see sendingRef) + the render-state checks.
    if (sendingRef.current || isSending || onCooldown) return;
    sendingRef.current = true;
    setIsSending(true);
    try {
      // Session-based resend: the server reads the recipient from the signup
      // session (no email argument → no enumeration surface) and no-ops if the
      // address is already verified, so it can't spam a verified inbox. The
      // response carries the server's cooldown window, which drives our timer.
      const res = await resendVerification();
      setSentOnce(true);
      showSuccess(res, 'Verification email sent', 'Please check your inbox to continue.');
      await startCooldown(res.retryAfterSeconds);
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 401) {
        setSessionLost(true);
      } else if (status === 429) {
        // Hit the backend rate limit (e.g. another tab already resent, or the
        // persisted cooldown was cleared). Mirror the server: read Retry-After
        // and resume the countdown from exactly what it reports.
        const retryAfter = parseInt(String(err?.response?.headers?.['retry-after'] ?? ''), 10);
        await startCooldown(Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : RESEND_COOLDOWN_MS / 1000);
      } else {
        handleApiError(err, 'Could not resend', 'Something went wrong. Please try again in a moment.');
      }
    } finally {
      setIsSending(false);
      sendingRef.current = false;
    }
  };

  const resendDisabled = isSending || onCooldown;

  return (
    <View style={[styles.root, { backgroundColor: colors.paper }]}>
      <ScrollView contentContainerStyle={styles.scrollContent} keyboardShouldPersistTaps="handled">
        <View style={[styles.card, { backgroundColor: colors.card, borderColor: colors.line }]}>
          <AuthBrandHeader />

          <View style={[styles.iconCircle, { backgroundColor: colors.accentSoft, borderColor: colors.accentLine }]}><Text style={styles.icon}>🚀</Text></View>

          <Text style={[styles.eyebrow, { color: colors.accent }]}>WELCOME ABOARD</Text>
          <Text style={[styles.title, { color: colors.accent }]}>Verify your email to continue</Text>
          <Text style={[styles.sub, { color: colors.ink2 }]}>
            Your account is created — one quick step and you're in. We just need to confirm it's
            really you.
          </Text>

          <View style={[styles.emailPill, { backgroundColor: colors.accentSoft, borderColor: colors.accentLine }]}>
            <Text style={[styles.emailPillLabel, { color: colors.accent }]}>We sent your verification link to</Text>
            <Text style={[styles.emailPillValue, { color: colors.ink }]}>{email || 'your registered email'}</Text>
          </View>

          <View style={[styles.infoBox, { backgroundColor: colors.paper2, borderColor: colors.line }]}>
            <Text style={[styles.infoText, { color: colors.ink2 }]}>
              Open that email and tap the link to verify your address. This page will move you on to
              sign in automatically the moment it's verified.
            </Text>
          </View>

          {sentOnce && !sessionLost && (
            <View style={[styles.sentBox, { backgroundColor: colors.successSoft, borderColor: colors.successLine }]}>
              <Text style={[styles.sentText, { color: colors.success }]}>
                ✓ A fresh verification link is on its way. Open it to activate your account.
              </Text>
            </View>
          )}

          {/* Resend — always available. States: sending, cooldown, or ready. */}
          {isSending ? (
            <View style={styles.resendRow}>
              <View style={styles.resendBusy}>
                <ActivityIndicator size="small" color={colors.accent} />
                <Text style={[styles.resendLink, { color: colors.ink4 }]}>Sending…</Text>
              </View>
            </View>
          ) : onCooldown ? (
            // Premium cooldown: a labelled timer pill over a progress bar that
            // fills as the server's resend window (3 min) elapses.
            <View style={styles.cooldownWrap}>
              <View style={[styles.cooldownPill, { backgroundColor: colors.accentSoft, borderColor: colors.accentLine }]}>
                <Text style={styles.cooldownIcon}>⏳</Text>
                <Text style={[styles.cooldownLabel, { color: colors.ink3 }]}>Resend available in</Text>
                <Text style={[styles.cooldownTime, { color: colors.accent }]}>{formatRemaining(remainingMs)}</Text>
              </View>
              <View style={[styles.cooldownTrack, { backgroundColor: colors.line }]}>
                <View
                  style={[
                    styles.cooldownFill,
                    { backgroundColor: colors.accent, width: `${cooldownPct}%` },
                    Platform.OS === 'web' ? ({ transition: 'width 1s linear' } as any) : {},
                  ]}
                />
              </View>
            </View>
          ) : (
            <View style={styles.resendRow}>
              <Text style={[styles.resendLabel, { color: colors.ink4 }]}>Didn't get it?</Text>
              <Pressable
                onPress={handleResend}
                disabled={resendDisabled}
                accessibilityRole="link"
                style={({ hovered }: any) => [
                  styles.resendPill,
                  { borderColor: colors.accentLine, backgroundColor: hovered ? colors.accentSoft : 'transparent' },
                ]}
              >
                <Text style={styles.resendPillIcon}>✉️</Text>
                <Text style={[styles.resendLink, { color: colors.accent }]}>Resend verification email</Text>
              </Pressable>
            </View>
          )}

          {/* Recovery path shown in addition to the resend link when the signup
              session has expired (resend needs that session to reach the server). */}
          {sessionLost && (
            <View style={styles.resendRow}>
              <Text style={[styles.resendLabel, { color: colors.ink4 }]}>Session expired — please</Text>
              <Pressable onPress={() => advanceToLogin()} accessibilityRole="link">
                <Text style={[styles.resendLink, { color: colors.accent }]}>sign in again</Text>
              </Pressable>
            </View>
          )}

          <Text style={[styles.hint, { color: colors.ink4 }]}>The link stays valid for 15 minutes. Can't find it? Check your spam folder.</Text>
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  root: {
    flex: 1,
    ...(Platform.OS === 'web' ? ({ minHeight: '100vh' } as any) : {}),
  },
  scrollContent: {
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 40,
    paddingHorizontal: 16,
  },
  card: {
    width: '100%',
    maxWidth: CONTENT.form,
    borderRadius: 24,
    padding: 32,
    borderWidth: 1,
    alignItems: 'center',
    overflow: 'hidden',
    ...(Platform.OS === 'web'
      ? ({ boxShadow: '0 8px 40px rgba(15,23,42,0.10)' } as any)
      : {
          shadowColor: '#0F172A',
          shadowOffset: { width: 0, height: 8 },
          shadowOpacity: 0.1,
          shadowRadius: 24,
          elevation: 8,
        }),
  },
  iconCircle: {
    width: 60, height: 60, borderRadius: 30,
    borderWidth: 1, alignItems: 'center', justifyContent: 'center', marginBottom: 16,
  },
  icon: { fontSize: 28 },
  eyebrow: {
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 2,
    marginBottom: 6,
  },
  title: {
    fontSize: 28,
    fontWeight: '900',
    marginBottom: 10,
    textAlign: 'center',
    letterSpacing: -0.8,
    lineHeight: 34,
  },
  sub: {
    fontSize: 14,
    fontWeight: '500',
    textAlign: 'center',
    lineHeight: 22,
    marginBottom: 18,
  },
  emailPill: {
    width: '100%',
    borderWidth: 1,
    borderRadius: 14,
    paddingVertical: 12,
    paddingHorizontal: 16,
    alignItems: 'center',
    marginBottom: 14,
  },
  emailPillLabel: { fontSize: 12, fontWeight: '600', marginBottom: 2 },
  emailPillValue: { fontSize: 15, fontWeight: '800', letterSpacing: -0.2 },
  infoBox: {
    borderWidth: 1,
    borderRadius: 12,
    padding: 14,
    width: '100%',
    marginBottom: 12,
  },
  infoText: { fontSize: 13, lineHeight: 20, textAlign: 'center' },
  sentBox: {
    width: '100%',
    borderWidth: 1,
    borderRadius: 12,
    padding: 14,
    marginBottom: 12,
  },
  sentText: { fontSize: 13, lineHeight: 20, fontWeight: '600', textAlign: 'center' },
  resendRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    marginBottom: 10,
  },
  resendBusy: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  resendLabel: { fontSize: 13, fontWeight: '500' },
  resendLink: { fontSize: 13, fontWeight: '700' },
  // Ready-state resend as a subtle outlined pill (premium hover fill on web).
  resendPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    borderWidth: 1,
    borderRadius: 999,
    paddingHorizontal: 14,
    paddingVertical: 7,
    ...(Platform.OS === 'web' ? ({ transition: 'background-color 0.15s ease' } as any) : {}),
  },
  resendPillIcon: { fontSize: 13 },
  // Premium cooldown: timer pill + progress track.
  cooldownWrap: { width: '100%', alignItems: 'center', gap: 10, marginBottom: 10 },
  cooldownPill: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    borderWidth: 1,
    borderRadius: 999,
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  cooldownIcon: { fontSize: 14 },
  cooldownLabel: { fontSize: 12.5, fontWeight: '600' },
  cooldownTime: {
    fontSize: 15,
    fontWeight: '800',
    letterSpacing: 0.5,
    fontFamily: Platform.OS === 'web' ? 'IBM Plex Mono, monospace' : undefined,
    ...(Platform.OS === 'web' ? ({ fontVariantNumeric: 'tabular-nums' } as any) : {}),
  },
  cooldownTrack: {
    height: 5,
    width: '72%',
    borderRadius: 3,
    overflow: 'hidden',
  },
  cooldownFill: { height: '100%', borderRadius: 3 },
  hint: { fontSize: 12, textAlign: 'center', marginBottom: 4, lineHeight: 18 },
});

export default Check_Email_Screen;
